import React from 'react';
import { Badge, Box } from '@chakra-ui/react';

// Semáforo de SLA do card (task #761). O cálculo (Fibonacci em dias corridos,
// normalização, fuso) é feito no BACKEND; o front só pinta o resultado.

/** Cor do semáforo, calculada pelo backend. */
export type SlaCor = 'verde' | 'amarelo' | 'vermelho';

/** Shape do SLA serializado pelo card (backend). `null` = card sem SLA. */
export interface SlaInfo {
  cor: SlaCor;
  /** Texto amigável ("Dentro do SLA" / "Perto do SLA" / "No limite do SLA" / "SLA atingido"). */
  label: string;
  /** Dias corridos de hoje até o SLA (0 se vencido; nome histórico do campo). */
  dias_uteis_restantes: number;
  /** True se o card já passou da data de SLA (vermelho fixo). */
  vencido: boolean;
}

// Paleta do semáforo — escala de 4 níveis (verde → amarelo → laranja → vermelho).
// verde=No prazo · amarelo=Atenção · vermelho=Atrasado. O `vermelho` do backend, quando
// NÃO vencido, é "Crítico" e ganha tom próprio (ESTILO_CRITICO, laranja escuro) — fica
// ENTRE Atenção e Atrasado. O backend não distingue: vem `cor='vermelho'` + o flag `vencido`.
const ESTILOS: Record<SlaCor, { bg: string; color: string; dot: string }> = {
  verde: { bg: '#E5F5ED', color: '#16A34A', dot: '#16A34A' },
  amarelo: { bg: '#FEF3C7', color: '#F59E0B', dot: '#F59E0B' },
  vermelho: { bg: '#FBE9E7', color: '#DC2626', dot: '#DC2626' },
};
const ESTILO_CRITICO = { bg: '#FFEDD5', color: '#EA580C', dot: '#EA580C' };

// Rótulo curto, alinhado à LEGENDA do board (No prazo / Atenção / Atrasado), em vez do
// jargão do backend ("Dentro do SLA" etc.). Vermelho não-vencido = "Crítico" (perto do
// prazo, mas ainda não atrasado); vencido = "Atrasado".
const ROTULO_CURTO: Record<SlaCor, string> = {
  verde: 'No prazo',
  amarelo: 'Atenção',
  vermelho: 'Atrasado',
};

interface SlaBadgeProps {
  /** Dados de SLA do card. `null`/`undefined` → não renderiza (card sem SLA). */
  sla?: SlaInfo | null;
  /** Exibe os dias restantes ao lado do label (default true). */
  mostrarDias?: boolean;
  /** Usa o rótulo curto consistente com a legenda (No prazo/Atenção/Atrasado). */
  rotuloCurto?: boolean;
}

/**
 * Badge do semáforo de SLA. Card sem SLA não renderiza nada. Quando não está vencido e
 * `mostrarDias`, anexa "· N dias" (dias corridos restantes). Com `rotuloCurto`, usa o
 * vocabulário da legenda (No prazo/Atenção/Atrasado) no lugar do label técnico do backend.
 */
export function SlaBadge({ sla, mostrarDias = true, rotuloCurto = false }: SlaBadgeProps) {
  if (!sla) return null;

  const critico = sla.cor === 'vermelho' && !sla.vencido;
  const estilo = critico ? ESTILO_CRITICO : (ESTILOS[sla.cor] ?? ESTILOS.verde);
  // `rotuloCurto` (cards/listas compactas) mostra só "Nd" (ex.: "15d") — sem "úteis": este é o
  // semáforo interno do kanban (#761), não o prazo legal da junta (esse é o badge "Prazo: N dias").
  // Compacto p/ não quebrar linha e empurrar o card; a contagem exata fica no tooltip (title).
  const unidadeLonga = sla.dias_uteis_restantes === 1 ? 'dia' : 'dias';
  const trechoDias = rotuloCurto
    ? `${sla.dias_uteis_restantes}d`
    : `${sla.dias_uteis_restantes} ${unidadeLonga}`;
  const sufixoDias = mostrarDias && !sla.vencido ? ` · ${trechoDias}` : '';
  const vermelhoLabel = sla.vencido ? ROTULO_CURTO.vermelho : 'Crítico';
  const labelCurto = sla.cor === 'vermelho' ? vermelhoLabel : ROTULO_CURTO[sla.cor];
  const label = rotuloCurto ? labelCurto : sla.label;

  return (
    <Badge
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      style={{ backgroundColor: estilo.bg, color: estilo.color }}
      borderRadius="full"
      px={2.5}
      py={0.5}
      fontSize="11.5px"
      fontWeight={700}
      textTransform="none"
      title={sla.label}
    >
      <Box w="6px" h="6px" borderRadius="full" style={{ background: estilo.dot }} />
      {label}
      {sufixoDias}
    </Badge>
  );
}
